import React, { FC, useEffect, useRef, useState } from 'react';
import { apiService } from 'utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatTime } from 'utils/functions';
import moment from 'moment';
import { dateDifference } from 'utils/functions';
import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { ALL_POLYGONS_ID } from 'utils/constants';
import PolygonSelector from 'components/base/polygon-selector';
import useDashboardContext from 'contexts/dashboard.context';

const AvgDeliveryTimeChart: FC = () => {
  const { activeCokitchen } = useDashboardContext();
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
  );

  const didMount = useRef(false);
  const [chartData, setChartData] = useState([] as any);
  const [filters, setFilters] = useState({
    polygonId: '',
    dateRange: getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK)
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    }
  };

  useEffect(() => {
    if (activeCokitchen?.id) setFilters({ ...filters, polygonId: '' });
  }, [activeCokitchen]);

  useEffect(() => {
    if (didMount.current) getChartData();
    didMount.current = true;
  }, [filters]);

  const getChartData = async () => {
    const {
      polygonId,
      dateRange: { startDate: start_time, endDate: end_time }
    } = filters;

    let interval = '';
    if (dateDifference(start_time, end_time) <= 1) {
      interval = 'hour';
    } else if (dateDifference(start_time, end_time) > 56) {
      interval = 'month';
    } else if (dateDifference(start_time, end_time) > 31) {
      interval = 'week';
    } else if (dateDifference(start_time, end_time) < 31) {
      interval = 'day';
    }
    const params: any = {
      start_date: start_time,
      end_date: end_time,
      interval,
      cokitchen_id: activeCokitchen.id
    };

    if (polygonId !== ALL_POLYGONS_ID && polygonId) params.cokitchen_polygon_id = polygonId;

    const res = await apiService.get('logistics/report/delivery-duration', {
      params,
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });

    const result = res.data?.data?.results ?? [];
    const labelsFormat = result?.map((item: any) => new Date(item.time));
    const labels = labelsFormat.map((item: any) => {
      return `${moment(item).format('DD/MM')} ${interval === 'hour' ? formatTime(item) : ''}`;
    });
    const avg_duration = result.map((item: any) => {
      return item.duration / item.count / 60;
    });

    setChartData({
      labels,
      datasets: [
        {
          fill: true,
          backgroundColor: '#FFEDF0',
          pointStyle: 'line',
          pointRadius: 1,
          borderWidth: 1,
          label: 'Time (seconds)',
          borderColor: '#FCB6C0',
          data: avg_duration
        }
      ]
    });
  };

  return (
    <div className='border rounded-lg border-[#DEDEDE] py-6'>
      <div className='flex flex-col lg:flex-row gap-4 justify-between mx-6 mb-5'>
        <p className='font-medium text-lg'>Avg. Delivery Time Chart</p>
        <div className='flex gap-2 grow lg:grow-0 justify-end'>
          <PolygonSelector
            hasAll
            polygonId={filters.polygonId}
            setPolygonId={(polygonId) => setFilters({ ...filters, polygonId })}
          />
          <DateRangeSelector
            showTime
            onChange={(v) => setFilters({ ...filters, dateRange: v.date })}
          />
        </div>
      </div>
      <hr className='text-[#DCE2EA] mb-5' />

      <div>
        {chartData.length !== 0 ? (
          <Line id='chart' options={options} data={chartData} height={400} />
        ) : (
          <p className='font-semibold text-center my-16'>Loading ...</p>
        )}
      </div>
    </div>
  );
};

export default AvgDeliveryTimeChart;
